import { useEffect } from "react";

/**
 * iOS(Safari·Chrome 공통 WebKit) 키보드 닫힘 후 화면이 위로 밀린 채 남는 문제 보정.
 *
 * 배경
 *  - 앱 셸(.app)은 `height:100dvh; overflow:hidden` 이고 스크롤은 `.content` 안에서만 일어남.
 *    → 문서(window) 자체는 원래 스크롤될 일이 없음.
 *  - 그런데 iOS 는 input/textarea 에 포커스가 가면 키보드 위로 입력란을 보이게 하려고
 *    문서(레이아웃 뷰포트)를 강제로 스크롤한다. 키보드가 닫힌 뒤 이 오프셋을 되돌리지 않는
 *    경우가 있어(특히 Chrome iOS) position:fixed 탭바·헤더가 허공에 뜨고 하단이 비는 증상.
 *    사용자는 body 를 스크롤할 수 없어 스스로 복구할 방법이 없다.
 *
 * 동작
 *  - 문서가 스크롤됐는데(window.scrollY ≠ 0) 편집 가능한 요소에 포커스가 없으면 0 으로 되돌린다.
 *  - 포커스 해제(focusout)·visualViewport 크기 복귀·window scroll 세 시점에 검사.
 *    키보드가 열려 있는 동안(입력란 포커스 중)은 건드리지 않아 입력란 가시성은 유지된다.
 */
const EDITABLE_SEL = "input, textarea, select, [contenteditable='true'], [contenteditable='']";

function isEditableFocused(): boolean {
  const el = document.activeElement;
  if (!el || el === document.body) return false;
  return el.matches(EDITABLE_SEL);
}

function resetIfDrifted() {
  if (isEditableFocused()) return;
  if (window.scrollY !== 0 || window.scrollX !== 0) window.scrollTo(0, 0);
  // iOS 가 html/body 에 scrollTop 을 남기는 경우도 함께 정리
  if (document.documentElement.scrollTop !== 0) document.documentElement.scrollTop = 0;
  if (document.body.scrollTop !== 0) document.body.scrollTop = 0;
}

export function useViewportScrollReset() {
  useEffect(() => {
    let timer: number | undefined;
    // 키보드 닫힘 애니메이션(약 250ms)이 끝난 뒤에도 한 번 더 확인
    const schedule = () => {
      window.clearTimeout(timer);
      requestAnimationFrame(resetIfDrifted);
      timer = window.setTimeout(resetIfDrifted, 320);
    };

    const onFocusOut = () => schedule();
    const onScroll = () => resetIfDrifted();
    const vv = window.visualViewport;
    const onVvResize = () => schedule();

    document.addEventListener("focusout", onFocusOut, true);
    window.addEventListener("scroll", onScroll, { passive: true });
    vv?.addEventListener("resize", onVvResize);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("focusout", onFocusOut, true);
      window.removeEventListener("scroll", onScroll);
      vv?.removeEventListener("resize", onVvResize);
    };
  }, []);
}
